import { useCallback, useRef, useState } from 'react';

/**
 * Hook to manage hover and keyboard navigation state
 * Provides synchronized hover and keyboard selection with proper precedence
 */
export const useHoverKeyboardNavigation = (itemCount: number) => {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
    const keyboardActiveRef = useRef(false);

    // Use hovered index if available and keyboard is not active, otherwise use selected index
    const activeIndex =
        !keyboardActiveRef.current && hoveredIndex !== null
            ? hoveredIndex
            : selectedIndex;

    const selectItem = useCallback(
        (index: number) => {
            if (index >= itemCount) {
                return;
            }
            return index;
        },
        [itemCount],
    );

    const upHandler = useCallback(() => {
        const newIndex = (selectedIndex + itemCount - 1) % itemCount;
        setSelectedIndex(newIndex);
        keyboardActiveRef.current = true;
        setHoveredIndex(null);
        return newIndex;
    }, [selectedIndex, itemCount]);

    const downHandler = useCallback(() => {
        const newIndex = (selectedIndex + 1) % itemCount;
        setSelectedIndex(newIndex);
        keyboardActiveRef.current = true;
        setHoveredIndex(null);
        return newIndex;
    }, [selectedIndex, itemCount]);

    const enterHandler = useCallback(() => {
        return activeIndex;
    }, [activeIndex]);

    const handleMouseEnter = useCallback((index: number) => {
        setHoveredIndex(index);
        keyboardActiveRef.current = false;
    }, []);

    const handleMouseLeave = useCallback(() => {
        setHoveredIndex(null);
    }, []);

    const reset = useCallback(() => {
        setSelectedIndex(0);
        setHoveredIndex(null);
        keyboardActiveRef.current = false;
    }, []);

    return {
        selectedIndex,
        hoveredIndex,
        activeIndex,
        selectItem,
        upHandler,
        downHandler,
        enterHandler,
        handleMouseEnter,
        handleMouseLeave,
        reset,
    };
};
